// TripReviewTab.jsx — "Review" tab inside a client's trip (ClientDetailModal).
// Shows where the trip's review stands, the client's answers exactly as they were asked,
// the email history, and buttons to send / remind / copy the link / email a test to yourself.
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useToast, useConfirm, Btn, Spinner, ApiError } from "../ui";
import { useAuth } from "../auth";
import { BRAND } from "../brand";
import {
  ReviewQuestions, ReviewStatusPill, LockedDetails, ConsentPanel, PublicUseBadge, errMsg, fmtDay, fmtDateTime,
} from "./reviewShared";

export default function TripReviewTab({ client, tripIndex, trip }) {
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth() || {};
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState(null); // "send" | "initial" | "test"
  const [testTo, setTestTo] = useState("");
  const [showTest, setShowTest] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get(`/crm/reviews/trip/${client._id}/${tripIndex}`);
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: errMsg(e), data: null });
    }
  }, [client._id, tripIndex]);

  // Reload when switching trips or when the trip's status changes (e.g. just marked completed).
  useEffect(() => { load(); }, [load, trip?.progressStatus]);

  useEffect(() => { setTestTo(user || ""); }, [user]);

  if (state.loading && !state.data) return <Spinner />;
  if (state.error) return <ApiError msg={state.error} onRetry={load} />;

  const { review, link, form, automation, trip: t } = state.data;
  const submitted = review?.status === "submitted";
  const sentBefore = !!review?.firstSentAt;
  const email = t?.email || client.email;

  const send = async (kind) => {
    const isReminder = kind === "reminder" || (!kind && sentBefore);
    const ok = await confirm({
      title: isReminder ? "Send a reminder?" : sentBefore ? "Resend the review email?" : "Send the review email?",
      message: `The review link goes to ${email}.`,
      confirmText: "Send",
      danger: false,
    });
    if (!ok) return;
    setBusy(kind || "send");
    try {
      const data = await api.post(`/crm/reviews/trip/${client._id}/${tripIndex}/send`, kind ? { kind } : {});
      setState({ loading: false, error: null, data });
      toast(isReminder ? "Reminder sent ✓" : "Review email sent ✓");
    } catch (e) {
      toast(errMsg(e), "error");
      load();
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast("Enter an email address", "error");
    setBusy("test");
    try {
      const res = await api.post("/crm/reviews/test-send", { to: testTo.trim(), clientId: client._id, tripIndex });
      const r = res.results?.[0];
      if (r?.ok) {
        toast(`Test sent to ${res.to} ✓`);
        setShowTest(false);
      } else {
        toast(r?.error || "Test failed", "error");
      }
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setBusy(null);
    }
  };

  const recordApproval = async (note) => {
    setBusy("approval");
    try {
      await api.post(`/crm/reviews/${state.data.review._id}/approval`, { by: user || "", note });
      toast("Approval recorded ✓");
      load();
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast("Link copied ✓");
    } catch {
      window.prompt("Copy the review link:", link);
    }
  };

  // ── What happens next (shown until the review is sent) ──
  let hint = null;
  if (!review || review.status === "pending") {
    const completed = t?.progressStatus === "completed";
    const beforeGoLive = completed && automation.goLiveAt &&
      (!t.completedAt || new Date(t.completedAt) < new Date(automation.goLiveAt));
    if (!automation.enabled) hint = "Automatic review emails are switched off (Reviews → Automation). You can send this one yourself.";
    else if (beforeGoLive) hint = "This trip was completed before automatic review emails were switched on, so no email went out. You can send it yourself.";
    else if (!completed) {
      hint = "The review email goes out automatically as soon as this trip is marked completed" +
        (automation.autoCompleteTrips && t?.tripEndDate ? ` — at the latest the day after it ends on ${fmtDay(t.tripEndDate)}.` : ".");
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Summary */}
      <div className="rounded-2xl border border-slate-100 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <ReviewStatusPill review={review} />
          <PublicUseBadge review={review} />
          <span className="text-xs text-slate-400">
            {form?.name || "Review"} questions · {form?.questionCount || 0} questions
          </span>
        </div>

        {review && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-4">
            <Fact label="Sent" value={fmtDateTime(review.firstSentAt) || "—"} />
            <Fact label="Opened" value={fmtDateTime(review.openedAt) || "—"} />
            <Fact label="Reminder" value={review.reminderSentAt ? fmtDateTime(review.reminderSentAt) : (sentBefore && !submitted && automation.sendReminders && automation.enabled ? `due after ${automation.reminderAfterDays} days` : "—")} />
            <Fact label="Submitted" value={fmtDateTime(review.submittedAt) || "—"} />
          </div>
        )}

        {review?.lastError && !submitted && (
          <div className="mb-4 px-3.5 py-2.5 rounded-xl bg-rose-50 border border-rose-100 text-xs text-rose-600">
            Last email failed: {review.lastError}
          </div>
        )}
        {hint && <p className="text-xs text-slate-500 mb-4">{hint}</p>}
        {!automation.linkReady && (
          <p className="text-xs text-amber-600 mb-4">Add the CRM web address in Reviews → Automation before sending — review links point there.</p>
        )}
        {!automation.emailConnected && (
          <p className="text-xs text-amber-600 mb-4">Review emails aren't connected on the server yet (sendReviewEmail in crm-email.service.ts).</p>
        )}

        {!submitted && (
          <div className="flex flex-wrap gap-2">
            {!sentBefore ? (
              <Btn size="sm" loading={busy === "send"} onClick={() => send()} disabled={!email}>
                Send review email
              </Btn>
            ) : (
              <>
                <Btn size="sm" loading={busy === "reminder"} onClick={() => send("reminder")}>Send reminder</Btn>
                <Btn size="sm" variant="secondary" loading={busy === "initial"} onClick={() => send("initial")}>Resend first email</Btn>
              </>
            )}
            {link && <Btn size="sm" variant="secondary" onClick={copyLink}>Copy link</Btn>}
            <Btn size="sm" variant="ghost" onClick={() => setShowTest((v) => !v)}>Email me a test</Btn>
          </div>
        )}

        {showTest && !submitted && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={`you@${BRAND.emailDomain || "example.com"}`}
              className="flex-1 min-w-[200px] px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none"
            />
            <Btn size="sm" loading={busy === "test"} onClick={sendTest}>Send test</Btn>
            <p className="w-full text-[11px] text-slate-400">
              Sends this trip's form to you only, marked [TEST]. Test answers never count as the client's review.
            </p>
          </div>
        )}
      </div>

      {/* Answers */}
      {submitted && (
        <div className="rounded-2xl border border-slate-100 p-4 sm:p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <h3 className="text-sm font-bold text-slate-800">Client's answers</h3>
            <span className="text-[11px] text-slate-400">
              As asked on {fmtDay(review.openedAt || review.submittedAt)}{review.formVersion ? ` · question set v${review.formVersion}` : ""}
            </span>
          </div>
          <div className="mb-5">
            <LockedDetails
              locked={{
                name: review.clientName, email: review.email, destination: review.destination,
                serviceType: review.tripTypeLabel, services: review.services,
                startDate: review.startDate, endDate: review.endDate,
              }}
            />
          </div>
          <div className="mb-5">
            <ConsentPanel review={review} onRecordApproval={recordApproval} busy={busy === "approval"} />
          </div>
          <ReviewQuestions questions={review.questions || []} />
        </div>
      )}

      {/* Email history */}
      {review?.emailLog?.length > 0 && (
        <div className="rounded-2xl border border-slate-100 p-4 sm:p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-3">Email history</h3>
          <ul className="flex flex-col gap-2">
            {[...review.emailLog].reverse().map((l, i) => (
              <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                <span className={`w-2 h-2 rounded-full ${l.ok ? "bg-emerald-400" : "bg-rose-400"}`} />
                <span className="text-slate-500 w-36">{fmtDateTime(l.at)}</span>
                <span className="font-medium text-slate-700 capitalize">{l.kind === "initial" ? "Review email" : l.kind}</span>
                <span className="text-slate-400">to {l.to}</span>
                <span className="text-slate-400">· {TRIGGER[l.trigger] || l.trigger}</span>
                {!l.ok && <span className="w-full pl-5 text-rose-500">{l.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const TRIGGER = {
  completion: "trip marked completed",
  package: "trip package completed",
  auto: "automatic",
  manual: "sent by staff",
  test: "test",
};

function Fact({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
      <span className="text-slate-700">{value}</span>
    </div>
  );
}
